import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import ClimatePanel from "@/components/ClimatePanel";
import DiagnosisCard from "@/components/DiagnosisCard";
import { api, API } from "@/lib/api";
import { ArrowLeft, Trash, FilePdf, Plant as PlantIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ScanDetail() {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/scans/${scanId}`);
        setScan(data);
      } catch {
        toast.error("Scan not found");
        navigate("/history");
      } finally {
        setLoading(false);
      }
    })();
  }, [scanId, navigate]);

  const remove = async () => {
    if (!window.confirm("Delete this scan?")) return;
    try {
      await api.delete(`/scans/${scanId}`);
      toast.success("Scan deleted");
      navigate("/history");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/scans/${scanId}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `climateleaf-scan-${scanId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Couldn't generate PDF");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand-50">
        <Navbar />
        <main className="max-w-5xl mx-auto px-6 py-12 font-mono-data text-mute animate-pulse-soft">loading scan…</main>
      </div>
    );
  }
  if (!scan) return null;

  return (
    <div className="min-h-screen bg-sand-50" data-testid="scan-detail-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link to="/history" className="btn-secondary !text-sm" data-testid="back-to-history"><ArrowLeft size={14} /> Back to history</Link>
          <div className="flex items-center gap-3">
            {scan.plant_id && (
              <Link to={`/plants/${scan.plant_id}`} className="text-sm text-forest hover:underline inline-flex items-center gap-1" data-testid="detail-plant-link">
                <PlantIcon size={14} weight="duotone" /> View plant timeline
              </Link>
            )}
            <button onClick={downloadPdf} className="btn-secondary !text-sm" data-testid="download-pdf">
              <FilePdf size={14} weight="duotone" /> Download PDF
            </button>
            <button onClick={remove} className="text-sm text-mute hover:text-terra inline-flex items-center gap-1" data-testid="detail-delete">
              <Trash size={14} /> Delete
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <div className="rounded-3xl overflow-hidden bg-sand-100 border border-sand-200 aspect-square">
              {scan.thumbnail_b64 && (
                <img src={`data:image/jpeg;base64,${scan.thumbnail_b64}`} alt="scan" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="font-mono-data text-mute text-xs mt-3 flex items-center justify-between">
              <span>{new Date(scan.created_at).toLocaleString()}</span>
              <span>{(scan.kind || "plant")} · {scan.capture_mode} · {scan.frame_count} frame(s)</span>
            </div>
          </div>
          <div className="md:col-span-7 space-y-5">
            <ClimatePanel climate={scan.climate} locationLabel={scan.location_label} />
            {scan.user_note && (
              <div className="bg-white/70 border border-sand-200 rounded-2xl p-4">
                <div className="font-mono-data text-mute mb-1">Your note</div>
                <p className="text-ink">{scan.user_note}</p>
              </div>
            )}
          </div>
        </div>

        <DiagnosisCard analysis={scan.analysis} climate={scan.climate} kind={scan.kind || "plant"} />
      </main>
    </div>
  );
}
